import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Users, ArrowLeft, Home, MapPin, BedDouble, 
  Heart, Shield, AlertTriangle, CheckCircle
} from "lucide-react";
import { API } from "@/App";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

const POSTCODES = ["E", "N", "NW", "SE", "SW", "W", "WC", "EC", "B"];
const PROPERTY_TYPES = [
  { value: "flat", label: "Flat" },
  { value: "house", label: "House" },
  { value: "maisonette", label: "Maisonette" },
  { value: "bungalow", label: "Bungalow" }
];
const BEDROOM_OPTIONS = [1, 2, 3, 4, 5, 6];

const MatchCard = ({ match, onInterest }) => {
  const [interested, setInterested] = useState(false);

  const handleInterest = async () => {
    setInterested(true);
    await onInterest(match.listing_id);
  };

  return (
    <Card className="bg-white border-gray-200 hover:border-[#05D38A] transition-all shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-[#05D38A]/10 text-[#05D38A] border-0 font-medium">
                {match.match_percent}% Match
              </Badge>
              {match.match_percent >= 90 && (
                <CheckCircle className="w-4 h-4 text-emerald-500" />
              )}
            </div>
            <p className="text-[#111827] font-medium">
              {match.current_bedrooms}-Bed {match.property_type?.charAt(0).toUpperCase() + match.property_type?.slice(1)}
            </p>
            <p className="text-gray-500 text-sm flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {match.display_location}
            </p>
          </div>
          <Home className="w-8 h-8 text-gray-300" />
        </div>
        
        <div className="flex gap-2 text-xs text-gray-500 mb-3">
          {match.has_garden && <span className="bg-gray-100 px-2 py-1 rounded">Garden</span>}
          {match.has_lift_access && <span className="bg-gray-100 px-2 py-1 rounded">Lift</span>}
          {match.floor_level && <span className="bg-gray-100 px-2 py-1 rounded">Floor {match.floor_level}</span>}
        </div>
        
        <Button
          onClick={handleInterest}
          disabled={interested}
          className={`w-full ${interested ? 'bg-gray-200 text-gray-500' : 'bg-[#05D38A] hover:bg-[#04B577] text-[#111827]'}`}
          size="sm"
          data-testid={`interest-btn-${match.listing_id}`}
        >
          {interested ? (
            <>
              <Heart className="w-4 h-4 mr-2 fill-current" />
              Interest Sent
            </>
          ) : (
            <>
              <Heart className="w-4 h-4 mr-2" />
              Interested
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default function ExchangeModule({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [myListing, setMyListing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    postcode_district: user?.postcode_district || "",
    current_bedrooms: user?.current_bedrooms?.toString() || "",
    wanted_bedrooms: "",
    property_type: user?.property_type || "",
    has_garden: false,
    has_lift_access: false,
    floor_level: ""
  });

  useEffect(() => {
    fetchMyListing();
    fetchMatches();
  }, []);

  const fetchMyListing = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/exchange/my-listing`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setMyListing(response.data.listing);
      if (response.data.listing) {
        setFormData({
          postcode_district: response.data.listing.postcode_district,
          current_bedrooms: response.data.listing.current_bedrooms.toString(),
          wanted_bedrooms: response.data.listing.wanted_bedrooms.toString(),
          property_type: response.data.listing.property_type,
          has_garden: response.data.listing.has_garden,
          has_lift_access: response.data.listing.has_lift_access,
          floor_level: response.data.listing.floor_level?.toString() || ""
        });
      }
    } catch (error) {
      console.error('Failed to fetch listing');
    }
  };

  const fetchMatches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/exchange/matches`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error('Failed to fetch matches');
    }
  };

  const handleSubmitListing = async () => {
    if (!formData.postcode_district || !formData.current_bedrooms || 
        !formData.wanted_bedrooms || !formData.property_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/exchange/listing`, {
        ...formData,
        current_bedrooms: parseInt(formData.current_bedrooms),
        wanted_bedrooms: parseInt(formData.wanted_bedrooms),
        floor_level: formData.floor_level ? parseInt(formData.floor_level) : null
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      
      toast.success(myListing ? "Listing updated!" : "Listing created! Now finding matches...");
      setShowForm(false);
      await fetchMyListing();
      await fetchMatches();
    } catch (error) {
      toast.error("Failed to save listing");
    } finally {
      setLoading(false);
    }
  };

  const handleInterest = async (listingId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/exchange/interest`, 
        { listing_id: listingId },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );
      
      if (response.data.mutual_match) {
        toast.success(response.data.message, {
          description: response.data.notice,
          duration: 8000
        });
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to express interest");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <TenUResLogo size="text-2xl" />
          <Button 
            variant="ghost" 
            className="text-[#111827] hover:text-[#05D38A]"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">
              Mutual Exchange
            </h1>
            <p className="text-gray-600 max-w-lg mx-auto">
              Find anonymous matches based on lifestyle, not just bricks. Your identity stays private.
            </p>
          </motion.div>

          {/* Privacy Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="border-[#05D38A]/30 bg-[#05D38A]/5">
              <CardContent className="p-4 flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#05D38A] mt-0.5 shrink-0" />
                <div>
                  <p className="text-[#111827] font-medium text-sm">Privacy Protected</p>
                  <p className="text-gray-600 text-sm">
                    No photos, house numbers, or street names are shared. All communication uses your UR-ID.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* My Listing Section */}
          {!showForm && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg text-[#111827]">Your Listing</CardTitle>
                  <CardDescription>
                    {myListing 
                      ? "Your property is listed for exchange" 
                      : "Create a listing to start finding matches"
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {myListing ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-3 text-sm">
                        <span className="bg-gray-100 px-3 py-1.5 rounded text-[#111827]">
                          <BedDouble className="w-4 h-4 inline mr-1" />
                          {myListing.current_bedrooms} bed → {myListing.wanted_bedrooms} bed
                        </span>
                        <span className="bg-gray-100 px-3 py-1.5 rounded text-[#111827]">
                          <MapPin className="w-4 h-4 inline mr-1" />
                          {myListing.postcode_district}
                        </span>
                        <span className="bg-gray-100 px-3 py-1.5 rounded text-[#111827] capitalize">
                          <Home className="w-4 h-4 inline mr-1" />
                          {myListing.property_type}
                        </span>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowForm(true)}
                        className="border-gray-300 text-[#111827]"
                        data-testid="edit-listing-btn"
                      >
                        Edit Listing
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      onClick={() => setShowForm(true)}
                      className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
                      data-testid="create-listing-btn"
                    >
                      Create Your Listing
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Create/Edit Listing Form */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg text-[#111827]">
                    {myListing ? "Edit Your Listing" : "Create Your Listing"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#111827]">Postcode District</Label>
                      <Select
                        value={formData.postcode_district}
                        onValueChange={(v) => setFormData({ ...formData, postcode_district: v })}
                      >
                        <SelectTrigger className="bg-white border-gray-300 text-[#111827]" data-testid="postcode-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {POSTCODES.map((code) => (
                            <SelectItem key={code} value={code} className="text-[#111827]">
                              {code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-[#111827]">Property Type</Label>
                      <Select
                        value={formData.property_type}
                        onValueChange={(v) => setFormData({ ...formData, property_type: v })}
                      >
                        <SelectTrigger className="bg-white border-gray-300 text-[#111827]" data-testid="property-type-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {PROPERTY_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value} className="text-[#111827]">
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-[#111827]">Current Bedrooms</Label>
                      <Select
                        value={formData.current_bedrooms}
                        onValueChange={(v) => setFormData({ ...formData, current_bedrooms: v })}
                      >
                        <SelectTrigger className="bg-white border-gray-300 text-[#111827]" data-testid="current-beds-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {BEDROOM_OPTIONS.map((num) => (
                            <SelectItem key={num} value={num.toString()} className="text-[#111827]">
                              {num}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-[#111827]">Wanted Bedrooms</Label>
                      <Select
                        value={formData.wanted_bedrooms}
                        onValueChange={(v) => setFormData({ ...formData, wanted_bedrooms: v })}
                      >
                        <SelectTrigger className="bg-white border-gray-300 text-[#111827]" data-testid="wanted-beds-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {BEDROOM_OPTIONS.map((num) => (
                            <SelectItem key={num} value={num.toString()} className="text-[#111827]">
                              {num}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <Label className="text-[#111827]">Has Garden</Label>
                      <Switch
                        checked={formData.has_garden}
                        onCheckedChange={(v) => setFormData({ ...formData, has_garden: v })}
                        data-testid="garden-switch"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <Label className="text-[#111827]">Lift Access</Label>
                      <Switch
                        checked={formData.has_lift_access}
                        onCheckedChange={(v) => setFormData({ ...formData, has_lift_access: v })}
                        data-testid="lift-switch"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-[#111827]">Floor Level (optional)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        placeholder="e.g., 3"
                        className="bg-white border-gray-300 text-[#111827]"
                        value={formData.floor_level}
                        onChange={(e) => setFormData({ ...formData, floor_level: e.target.value })}
                        data-testid="floor-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowForm(false)}
                      className="flex-1 border-gray-300 text-[#111827]"
                      data-testid="cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmitListing}
                      className="flex-1 bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
                      disabled={loading}
                      data-testid="save-listing-btn"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        "Save Listing"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Matches Section */}
          {!showForm && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-xl font-bold text-[#111827] mb-4">
                Potential Matches
              </h2>
              
              {matches.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {matches.map((match) => (
                    <MatchCard 
                      key={match.listing_id} 
                      match={match} 
                      onInterest={handleInterest}
                    />
                  ))}
                </div>
              ) : (
                <Card className="bg-white border-gray-200 shadow-lg">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {myListing 
                        ? "No matches found yet. Check back soon!"
                        : "Create a listing to start seeing matches."
                      }
                    </p>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* Safety Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <Card className="border-gray-200 bg-gray-50">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-gray-600 text-sm">
                    <strong className="text-[#111827]">Hard Exit:</strong> TenURes does not facilitate viewings or key exchanges. 
                    Once you find a match, contact your Housing Officer to proceed with the formal exchange process.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
